/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import get from 'lodash/get';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Portal from 'carbon-react/lib/components/portal';
import Tooltip from 'carbon-react/lib/components/tooltip';
import Icon from 'carbon-react/lib/components/icon';
import Button from 'carbon-react/lib/components/button';
import classNames from 'classnames';

import { UnpaidInvoiceNextPaymentInfo } from '@futrli-sdk/predict';
import { UIFeatherIcon } from '@futrli/ui-components';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from '@futrli/localisation';

import { INVOICE_DETAIL_KIND } from '../../../core/constants';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { Data } from '../../../utils/table-filters/filter-table-types';
import * as styles from '../virtualized-list.scss';
import * as paymentModalStyles from './payments-modal.scss'
import { EditPayments } from './edit-payments';
import { UserGridSettingsContext } from '../../../forecast/detail/reports/user-grid-settings/user-grid-settings-context';

interface Props {
  disabledExcludeInclude: boolean;
  checkDisabledRowActions: () => void;
  rowData?: Data;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  updateRow?: (...args: any[]) => Promise<void>;
  revalidateData?: () => Promise<void>;
  readOnly?: boolean;
}

export const EXCLUDE_INCLUDE_DISABLED_KEY = 'disable-include-exclude';

export const InvoicesAndBillsTableRow = ({
  rowData,
  updateRow,
  revalidateData,
  disabledExcludeInclude,
  checkDisabledRowActions,
  readOnly = false
}: Props) => {
  const intl = useIntl();
  const [checkboxPosition, setCheckboxPosition] = useState({ top: 0, left: 0 });

  const nextPaymentInfo = (get(rowData, 'next_payment_info') as unknown) as UnpaidInvoiceNextPaymentInfo;
  const refRow = useRef<HTMLDivElement>(null);

  const { currency } = useContext(PredictManagementContext);
  const [rowDisabled, setRowDisabled] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const {
    bulkUpdatesInvoicesBills: {
      showInvoicesBillsCheckboxes,
      selectedInvoicesBills,
      handleChangeSelectedInvoicesBills,
      isCustomSelect
    }
  } = useContext(UserGridSettingsContext);

  const showCheckboxes = showInvoicesBillsCheckboxes;

  const rowTypeLabel = useMemo(
    () => ({
      [INVOICE_DETAIL_KIND.INVOICE]: intl.formatMessage({ id: 'predict.invoice', defaultMessage: 'Invoice' }),
      [INVOICE_DETAIL_KIND.BILL]: intl.formatMessage({ id: 'predict.bill', defaultMessage: 'Bill' })
    }),
    [intl]
  );

  useEffect(() => {
    if (showCheckboxes && refRow.current) {
      const rect = refRow.current.getBoundingClientRect();
      setCheckboxPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [showCheckboxes]);

  const handleIncludeExcludeButtonClick = async (excluded_from_report: boolean) => {
    if (!readOnly && rowData && updateRow) {
      setRowDisabled(true);
      localStorage.setItem(EXCLUDE_INCLUDE_DISABLED_KEY, 'true');
      checkDisabledRowActions();
      trackEvent(TRACKING_EVENTS.EDITED_INVOICE);

      await updateRow(
        { excluded_from_report: !excluded_from_report },
        rowData?.id,
        rowData.type as INVOICE_DETAIL_KIND
      );

      localStorage.removeItem(EXCLUDE_INCLUDE_DISABLED_KEY);
      checkDisabledRowActions();
      setRowDisabled(false);
    }
  };

  const handleInvoiceBillCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, rowId: string) => {
    if (event.target.checked) {
      handleChangeSelectedInvoicesBills([...selectedInvoicesBills, rowId]);
    } else {
      handleChangeSelectedInvoicesBills(selectedInvoicesBills.filter(id => id !== rowId));
    }
  };

  const getIncludeExcludeButton = (isExcluded: boolean, id: string): JSX.Element => {
    if (isExcluded) {
      return (
        <Button
          data-testid={`include-button-${id}`}
          className={classNames(styles.excludeButton, { [styles.disabledIncludeExclude]: disabledExcludeInclude })}
          onClick={() => handleIncludeExcludeButtonClick(isExcluded)}
          disabled={disabledExcludeInclude}
          size="small"
        >
          <FormattedMessage id="common.include" defaultMessage="Include" />
        </Button>
      );
    }

    return (
      <Button
        data-testid={`exclude-button-${id}`}
        className={classNames(styles.excludeButton, { [styles.disabledIncludeExclude]: disabledExcludeInclude })}
        onClick={() => handleIncludeExcludeButtonClick(isExcluded)}
        disabled={disabledExcludeInclude}
        size="small"
      >
        <FormattedMessage id="common.exclude" defaultMessage="Exclude" />
      </Button>
    );
  };

  const handleCalendarClick = () => setShowPaymentsModal(true);

  const closePaymentsModal = () => setShowPaymentsModal(false);

  if (!rowData) {
    return null;
  }

  return (
    <>
      {showCheckboxes && !readOnly && (
        <Portal>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: checkboxPosition.top + 10,
              left: checkboxPosition.left - 32,
              zIndex: 0
            }}
          >
            <Checkbox
              checked={!!selectedInvoicesBills.some(id => id === rowData.id)}
              onChange={e => handleInvoiceBillCheckboxChange(e, rowData.id)}
              disabled={!isCustomSelect}
              aria-label={intl.formatMessage(
                {
                  id: 'predict.select_invoice_bill',
                  defaultMessage: `Select {type}`
                },
                { type: rowData.type === INVOICE_DETAIL_KIND.INVOICE ? 'invoice' : 'bill' }
              )}
            />
          </div>
        </Portal>
      )}
      <div
        role="row"
        key={rowData.id}
        className={clsx(styles.virtualizedListRow, {
          [styles.disableRow]: rowDisabled,
          [styles.excludedRow]: !!rowData.excluded_from_report
        })}
        ref={refRow}
      >
        <div
          role="cell"
          className={rowData.type === INVOICE_DETAIL_KIND.INVOICE ? styles.invoicesBorderDiv : styles.billsBorderDiv}
        />
        <div role="cell" className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont)}>
          {rowTypeLabel[(rowData.type as unknown) as INVOICE_DETAIL_KIND]}
        </div>
        <Tooltip message={<div>{rowData.contact_name}</div>} position="bottom">
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex2, styles.tableBodyFont)}
            style={{ minWidth: 0 }}
          >
            <span className={styles.textOverflow}>{rowData.contact_name}</span>
          </div>
        </Tooltip>
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        >
          <FormattedNumber
            style="currency"
            currency={currency!}
            value={Number(rowData.converted_amount_total)}
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        </div>
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        >
          <FormattedNumber
            style="currency"
            currency={currency!}
            value={Number(rowData.converted_amount_due)}
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        </div>
        <div
          role="cell"
          className={clsx(styles.statusCell, styles.virtualizedListCell, styles.flex4, styles.tableBodyFont, {
            [styles.billData]: nextPaymentInfo.days === 0 && rowData.type === INVOICE_DETAIL_KIND.BILL,
            [styles.invoiceData]: nextPaymentInfo.days === 0 && rowData.type === INVOICE_DETAIL_KIND.INVOICE
          })}
          // className={clsx(styles.statusCell, styles.virtualizedListCell, styles.flex4, styles.tableBodyFont)}
        >
          <div className={clsx(styles.flexSpaceBetween, styles.flex1)}>
            <div style={{ minWidth: 124 }}>
              {nextPaymentInfo.days > 0 ? (
                <FormattedMessage
                  id="predict.in_n_days_on"
                  defaultMessage="In {dayNum, plural, one {# day} other {# days}} on"
                  values={{ dayNum: nextPaymentInfo.days }}
                />
              ) : nextPaymentInfo.is_overdue ? (
                <FormattedMessage id="predict.today_overdue_on" defaultMessage="Today (overdue) on" />
              ) : (
                <FormattedMessage id="predict.today_on" defaultMessage="Today on" />
              )}
            </div>
            <div
              className={clsx({
                [styles.tableBoldBodyFont]: !rowData.excluded_from_report
              })}
              style={{ minWidth: 75, marginLeft: 5 }}
            >
              <FormattedDate value={nextPaymentInfo.date} month={'short'} year="2-digit" day="2-digit" />
            </div>
            {!readOnly && <div className={paymentModalStyles.iconButtonContainer}>
              <Button
                buttonType="tertiary"
                p={0}
                size="small"
                onClick={handleCalendarClick}
                aria-label={intl.formatMessage({
                  id: 'common.open_calendar.aria_label',
                  defaultMessage: 'Open calendar button'
                })}
              >
                <Icon type="calendar" />
              </Button>
            </div>}
            {!readOnly && getIncludeExcludeButton(!!rowData.excluded_from_report, rowData.id)}

            {nextPaymentInfo.is_overdue ? (
              <div className={styles.errorIconContainer} onClick={readOnly ? undefined : handleCalendarClick}>
                <UIFeatherIcon iconName="alert-circle" />
              </div>
            ) : (
              <div style={{ width: 20 }} />
            )}
          </div>
        </div>
        <div role="cell" className={clsx(styles.virtualizedListCell, styles.flex2, styles.tableBodyFont)}>
          {rowData.invoice_number}
        </div>
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex1, {
            [styles.tableBoldBodyFont]: !rowData.excluded_from_report
          })}
        >
          <FormattedDate value={rowData.issue_date} month={'short'} year="2-digit" day="2-digit" />
        </div>
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex1, {
            [styles.tableBoldBodyFont]: !rowData.excluded_from_report
          })}
        >
          <FormattedDate value={rowData.due_date} month={'short'} year="2-digit" day="2-digit" />
        </div>
      </div>
      {showPaymentsModal && !readOnly && (
        <EditPayments
          openDialog={showPaymentsModal}
          onCloseDialog={closePaymentsModal}
          data={rowData}
          revalidateData={revalidateData}
        />
      )}
    </>
  );
};
